/**
 * 🏛️ GuildProgressionPage — Standard Shop + Glimmers Sanctum (Feb 2026)
 *
 * Levels 3-5 progression wave. Users spend Contribution Points to buy
 * Luck Stones and Glimmers. Glimmers can also be paid with Phoenix
 * Blessings earned from the Chronophage Attack event.
 *
 * Owner canon Minimum Gate Rule: sub-multiple contributions still fund
 * the Guild Wealth pool but earn ZERO personal CP.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Coins, Sparkles, Lock, ShieldCheck, Info,
  ArrowRight, Trophy, Zap, Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ─── Helpers ───────────────────────────────────────────────
function fmtNum(n) {
  return new Intl.NumberFormat("en-US").format(Math.floor(n || 0));
}

function fmtPct(numer, denom) {
  if (!denom || denom <= 0) return 0;
  return Math.min(100, Math.round((numer / denom) * 100));
}


// ─── Contribute Panel ─────────────────────────────────────
function ContributePanel({ state, onDone }) {
  const [chits, setChits] = useState("");
  const [od, setOd] = useState("");
  const [busy, setBusy] = useState(false);

  const chitsN = parseInt(chits || 0, 10) || 0;
  const odN = parseInt(od || 0, 10) || 0;
  const cpChits = Math.floor(chitsN / (state.conversion_rates?.chits_per_cp || 1000));
  const cpOd    = Math.floor(odN    / (state.conversion_rates?.od_per_cp    || 5));
  const cpTotal = cpChits + cpOd;
  const chitsRem = chitsN - cpChits * (state.conversion_rates?.chits_per_cp || 1000);
  const odRem    = odN    - cpOd    * (state.conversion_rates?.od_per_cp    || 5);
  const anyRemainder = chitsRem > 0 || odRem > 0;

  const submit = async () => {
    if (chitsN === 0 && odN === 0) {
      toast.error("Enter Chits or OD to contribute.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/guild/progression/contribute`,
        { chits: chitsN, od: odN },
        { withCredentials: true }
      );
      if (r.data.cp_earned > 0) {
        toast.success(`+${r.data.cp_earned} CP · +${fmtNum(r.data.pool_gain_display)} Wealth`);
      } else if (r.data.gate_short) {
        toast.warning("Minimum Gate — contribution went to pool but earned 0 personal CP");
      }
      setChits("");
      setOd("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Contribution failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-950/20 via-black/40 to-amber-950/20 p-4" data-testid="guild-progression-contribute">
      <div className="flex items-center gap-2 mb-3">
        <Coins size={16} className="text-amber-300" />
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300">
          Contribute to the Guild
        </span>
      </div>

      <div className="text-[11px] text-neutral-400 mb-3">
        <span className="text-amber-200">1,000 Chits</span> or <span className="text-amber-200">5 Omni Dollars</span> = <span className="text-emerald-300 font-bold">1 CP</span>.
        Sub-multiples still fund the Guild Wealth pool but earn 0 personal CP.
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Chits ({fmtNum(state.wallets?.chits)} avail)</label>
          <input
            type="number"
            min="0"
            value={chits}
            onChange={(e) => setChits(e.target.value)}
            data-testid="guild-progression-input-chits"
            className="w-full px-3 py-2 rounded border border-neutral-700 bg-black/40 text-white text-sm font-mono"
            placeholder="e.g. 1000"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Omni Dollars ({fmtNum(state.wallets?.omni_dollars)} avail)</label>
          <input
            type="number"
            min="0"
            value={od}
            onChange={(e) => setOd(e.target.value)}
            data-testid="guild-progression-input-od"
            className="w-full px-3 py-2 rounded border border-neutral-700 bg-black/40 text-white text-sm font-mono"
            placeholder="e.g. 5"
          />
        </div>
      </div>

      {/* Live preview */}
      {(chitsN > 0 || odN > 0) && (
        <div className="mb-3 rounded-lg border border-neutral-800 bg-black/60 p-2.5 text-[11px]" data-testid="guild-progression-preview">
          <div className="flex justify-between text-neutral-400">
            <span>Personal CP</span>
            <span className={cpTotal > 0 ? "text-emerald-300 font-bold" : "text-red-400"}>+{cpTotal}</span>
          </div>
          <div className="flex justify-between text-neutral-400">
            <span>Guild Wealth pool</span>
            <span className="text-amber-300">+{fmtNum((chitsN * 1) + (odN * 200))} raw ({fmtNum(((chitsN * 1) + (odN * 200)) / 1000)} display)</span>
          </div>
          {anyRemainder && (
            <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-neutral-800 text-yellow-300/90">
              <Info size={11} className="mt-0.5 flex-shrink-0" />
              <span>
                Minimum Gate: {chitsRem > 0 && <>{fmtNum(chitsRem)} Chits</>}
                {chitsRem > 0 && odRem > 0 && " · "}
                {odRem > 0 && <>{fmtNum(odRem)} OD</>}
                {" "}under the multiple — funds the pool but earn 0 personal CP.
              </span>
            </div>
          )}
        </div>
      )}

      <button
        onClick={submit}
        disabled={busy || (chitsN === 0 && odN === 0)}
        data-testid="guild-progression-submit-contribute"
        className={`w-full py-2.5 rounded-lg font-bold uppercase tracking-widest text-xs transition ${
          busy || (chitsN === 0 && odN === 0)
            ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
            : "bg-gradient-to-r from-amber-600 to-yellow-500 text-black hover:brightness-110"
        }`}
      >
        {busy ? "Contributing..." : "Contribute →"}
      </button>
    </div>
  );
}


// ─── Standard Shop Card ────────────────────────────────────
function ShopItemCard({ item, cp, onBuy }) {
  const affordable = cp >= item.cost_cp;
  const unlocked   = item.unlocked;
  const disabled   = !unlocked || !affordable;
  return (
    <div className={`rounded-xl border-2 p-4 ${
      unlocked ? "border-emerald-500/30 bg-emerald-950/10" : "border-neutral-800 bg-black/30 opacity-70"
    }`} data-testid={`guild-shop-item-${item.id}`}>
      <div className="flex items-start gap-3 mb-2">
        <div className="text-4xl">{item.icon}</div>
        <div className="flex-1">
          <div className="text-sm font-bold text-white">{item.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Guild Hall Lv {item.hall_level_required}+ · Soulbound
          </div>
        </div>
      </div>
      <div className="text-[11px] text-neutral-400 leading-relaxed mb-3">{item.description}</div>
      <div className="flex items-center justify-between">
        <div className="text-sm">
          <span className="text-emerald-300 font-bold">{item.cost_cp}</span>
          <span className="text-neutral-500 text-[11px]"> CP</span>
        </div>
        <div className="text-[10px] text-neutral-500">Owned: {item.purchased || 0}</div>
      </div>
      <button
        onClick={() => onBuy(item.id)}
        disabled={disabled}
        data-testid={`guild-shop-buy-${item.id}`}
        className={`mt-3 w-full py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition ${
          disabled
            ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
            : "bg-emerald-600 text-white hover:bg-emerald-500"
        }`}
      >
        {!unlocked ? <><Lock size={11} className="inline mr-1" /> Locked · Hall Lv {item.hall_level_required}</> :
         !affordable ? "Need more CP" : "Buy"}
      </button>
    </div>
  );
}


// ─── Glimmer Card ──────────────────────────────────────────
function GlimmerCard({ glimmer, cp, pb, sanctumUnlocked, onBuy }) {
  const methods = glimmer.payment_methods || [];
  const isFree  = methods.length === 0 && !glimmer.owned;
  const dual    = methods.length > 1;
  const [paymentType, setPaymentType] = useState(methods[0] || "cp");
  // Auto-pin to the only allowed method if not dual
  useEffect(() => { if (methods.length === 1) setPaymentType(methods[0]); }, [glimmer.id]);

  const cost    = paymentType === "cp" ? glimmer.cost_cp : glimmer.cost_pb;
  const balance = paymentType === "cp" ? cp : pb;
  const affordable = balance >= cost;
  const owned   = glimmer.owned;
  const locked  = glimmer.locked_out;
  const disabled = owned || locked || !sanctumUnlocked || (!isFree && !affordable);

  const methodLabel = (m) => m === "cp" ? `${glimmer.cost_cp} CP` : `🔥 ${glimmer.cost_pb} PB`;

  return (
    <div className={`rounded-xl border-2 p-4 relative ${
      owned      ? "border-yellow-500/60 bg-yellow-950/15" :
      locked     ? "border-red-800/40 bg-red-950/10 opacity-70" :
      !sanctumUnlocked ? "border-neutral-800 bg-black/30 opacity-60" :
                   "border-purple-500/40 bg-purple-950/15"
    }`} data-testid={`guild-glimmer-${glimmer.id}`}>
      {owned && (
        <div className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold text-yellow-300 bg-yellow-900/40 border border-yellow-500/50 px-2 py-0.5 rounded">
          Owned
        </div>
      )}
      {locked && (
        <div className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold text-red-300 bg-red-900/40 border border-red-500/50 px-2 py-0.5 rounded">
          🚫 Locked Out
        </div>
      )}

      <div className="flex items-start gap-3 mb-2">
        <div className="text-4xl">{glimmer.icon}</div>
        <div className="flex-1">
          <div className="text-sm font-bold text-white">{glimmer.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            {isFree ? "Auto-Grant · Pet Lv 10" : "Soulbound"}
          </div>
        </div>
      </div>
      <div className="text-[11px] text-neutral-400 leading-relaxed mb-3 min-h-[3rem]">{glimmer.description}</div>

      {!owned && !locked && sanctumUnlocked && !isFree && (
        <>
          {dual ? (
            /* Dual-payment: user picks which currency to spend */
            <div className="flex rounded-md border border-neutral-700 overflow-hidden mb-2 text-[10px] uppercase tracking-widest">
              <button
                onClick={() => setPaymentType("cp")}
                className={`flex-1 py-1 font-bold ${paymentType === "cp" ? "bg-emerald-700 text-white" : "bg-black/40 text-neutral-400"}`}
                data-testid={`guild-glimmer-${glimmer.id}-pay-cp`}
              >
                {glimmer.cost_cp} CP
              </button>
              <button
                onClick={() => setPaymentType("pb")}
                className={`flex-1 py-1 font-bold ${paymentType === "pb" ? "bg-red-700 text-white" : "bg-black/40 text-neutral-400"}`}
                data-testid={`guild-glimmer-${glimmer.id}-pay-pb`}
              >
                🔥 {glimmer.cost_pb} PB
              </button>
            </div>
          ) : (
            /* Single-currency: just show the price locked in */
            <div className={`mb-2 rounded-md border px-2 py-1 text-[10px] uppercase tracking-widest text-center font-bold ${
              methods[0] === "cp"
                ? "border-emerald-500/40 bg-emerald-900/20 text-emerald-300"
                : "border-red-500/40 bg-red-900/20 text-red-300"
            }`} data-testid={`guild-glimmer-${glimmer.id}-pay-locked`}>
              {methodLabel(methods[0])} only
            </div>
          )}
        </>
      )}

      <button
        onClick={() => onBuy(glimmer.id, paymentType)}
        disabled={disabled}
        data-testid={`guild-glimmer-buy-${glimmer.id}`}
        className={`w-full py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition ${
          disabled
            ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
            : "bg-purple-600 text-white hover:bg-purple-500"
        }`}
      >
        {owned                ? "Owned" :
         locked               ? "Class Locked" :
         !sanctumUnlocked     ? <><Lock size={11} className="inline mr-1" /> Sanctum Locked</> :
         isFree               ? "Claim Free" :
         !affordable          ? `Need more ${paymentType === "cp" ? "CP" : "PB"}` :
                                `Buy · ${cost} ${paymentType === "cp" ? "CP" : "PB"}`}
      </button>
    </div>
  );
}


// ─── Contribution Leaderboard ─────────────────────────────
function ContributionLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/guild/progression/leaderboard?limit=5`, { withCredentials: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ leaderboard: [], self_rank: null }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;

  const board = data?.leaderboard || [];
  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="rounded-xl border-2 border-yellow-500/30 bg-gradient-to-br from-yellow-950/20 via-black/40 to-yellow-950/20 p-4" data-testid="guild-progression-leaderboard">
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={16} className="text-yellow-300" />
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-yellow-300">
          Contribution Leaderboard · Top 5 Lifetime
        </span>
        {data?.self_rank && (
          <span className="ml-auto text-[10px] text-yellow-300/80" data-testid="leaderboard-self-rank">
            You: #{data.self_rank}
          </span>
        )}
      </div>

      {board.length === 0 ? (
        <div className="text-center py-6 text-[11px] text-neutral-500 italic">
          No contributions yet — be the first to top the board.
        </div>
      ) : (
        <div className="space-y-1.5">
          {board.map((row) => (
            <div
              key={row.user_id || row.rank}
              data-testid={`leaderboard-row-${row.rank}`}
              className={`flex items-center gap-3 rounded-lg border p-2.5 ${
                row.is_self
                  ? "border-yellow-400/60 bg-yellow-500/10"
                  : "border-neutral-800 bg-black/40"
              }`}
            >
              <div className="text-2xl w-8 text-center">
                {medals[row.rank - 1] || <span className="text-neutral-500 text-base font-bold">#{row.rank}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold truncate ${row.is_self ? "text-yellow-200" : "text-white"}`}>
                    {row.name}
                  </span>
                  {row.is_self && (
                    <span className="text-[9px] uppercase tracking-widest bg-yellow-500/20 text-yellow-300 px-1.5 py-0.5 rounded font-bold">
                      You
                    </span>
                  )}
                </div>
                {row.hero_class && (
                  <div className="text-[10px] text-neutral-500 capitalize">{row.hero_class.replace(/_/g, " ")}</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-emerald-300 font-mono">{fmtNum(row.cp_lifetime)}</div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-500">Lifetime CP</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Main Page ─────────────────────────────────────────────
export default function GuildProgressionPage() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyUnlock, setBusyUnlock] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/guild/progression/state`, { withCredentials: true });
      setState(r.data);
    } catch (e) {
      toast.error("Could not load Guild Hall progression");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const wealthPct = useMemo(() => {
    if (!state?.guild) return 0;
    return fmtPct(state.guild.guild_wealth_display, state.unlock_cost);
  }, [state]);

  const buyShop = async (item_id) => {
    try {
      const r = await axios.post(`${API}/guild/progression/shop/buy`, { item_id }, { withCredentials: true });
      toast.success(`✅ ${r.data.bought.name} purchased`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    }
  };

  const buyGlimmer = async (glimmer_id, payment_type) => {
    try {
      const r = await axios.post(`${API}/guild/progression/glimmers/buy`,
        { glimmer_id, payment_type }, { withCredentials: true });
      toast.success(`✨ ${r.data.bought.name} unlocked`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    }
  };

  const doUnlockSanctum = async () => {
    setBusyUnlock(true);
    try {
      await axios.post(`${API}/guild/progression/glimmers-sanctum/unlock`, {}, { withCredentials: true });
      toast.success("🔓 Glimmers Sanctum unlocked!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    } finally {
      setBusyUnlock(false);
    }
  };

  if (!user)       return <div className="text-center py-16 text-neutral-500">Sign in to access the Guild Hall.</div>;
  if (loading)     return <div className="text-center py-16 text-neutral-500">Loading Guild Hall...</div>;
  if (!state?.in_guild) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 rounded-xl border border-neutral-800 bg-black/40 p-8" data-testid="guild-progression-nomembership">
        <Users size={40} className="text-neutral-500 mx-auto mb-3" />
        <div className="text-xl font-bold text-white mb-2">Join a Guild First</div>
        <div className="text-neutral-400 text-sm">{state?.message}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4" data-testid="guild-progression-root">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-amber-500/40 text-amber-300 mb-3">
          <Building2 size={14} />
          <span className="font-accent text-xs tracking-[0.3em] uppercase">
            {state.guild.name} · Hall Lv {state.guild.hall_level}
          </span>
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-white leading-[0.9] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Guild <span className="text-amber-300">Progression</span>
        </h2>
        <p className="text-neutral-400 text-sm max-w-2xl mx-auto">
          Contribute Chits &amp; OD to earn Contribution Points, spend them on Luck Stones,
          and unlock the Glimmers Sanctum with your guild&apos;s collective Wealth.
        </p>
      </div>

      {/* Currency dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="guild-progression-dashboard">
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-3 text-center">
          <div className="text-3xl">💠</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Contribution Pts</div>
          <div className="text-2xl font-bold text-emerald-300">{fmtNum(state.cp)}</div>
          <div className="text-[9px] text-neutral-600">Lifetime {fmtNum(state.cp_lifetime)}</div>
        </div>
        <div className="rounded-xl border border-red-500/30 bg-red-950/20 p-3 text-center">
          <div className="text-3xl">🔥</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Phoenix Blessings</div>
          <div className="text-2xl font-bold text-red-300">{fmtNum(state.wallets.phoenix_blessings)}</div>
          <div className="text-[9px] text-neutral-600">From Chronophage</div>
        </div>
        <div className="rounded-xl border border-yellow-500/30 bg-yellow-950/20 p-3 text-center">
          <div className="text-3xl">🪙</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Chits</div>
          <div className="text-2xl font-bold text-yellow-300">{fmtNum(state.wallets.chits)}</div>
        </div>
        <div className="rounded-xl border border-blue-500/30 bg-blue-950/20 p-3 text-center">
          <div className="text-3xl">💎</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Omni Dollars</div>
          <div className="text-2xl font-bold text-blue-300">{fmtNum(state.wallets.omni_dollars)}</div>
        </div>
        <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-3 text-center">
          <div className="text-3xl">🏛️</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Guild Wealth</div>
          <div className="text-2xl font-bold text-amber-300">{fmtNum(state.guild.guild_wealth_display)}</div>
          <div className="text-[9px] text-neutral-600">Raw {fmtNum(state.guild.guild_wealth_raw)}</div>
        </div>
      </div>

      {/* Contribution Leaderboard */}
      <div className="mb-6">
        <ContributionLeaderboard />
      </div>

      {/* Contribute + Sanctum progress side-by-side */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <ContributePanel state={state} onDone={load} />

        {/* Sanctum unlock progress */}
        <div className="rounded-xl border-2 border-purple-500/30 bg-gradient-to-br from-purple-950/30 via-black/40 to-purple-950/30 p-4" data-testid="guild-progression-sanctum">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-purple-300" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-purple-300">
              Glimmers Sanctum
            </span>
          </div>
          <div className="text-xl font-bold text-white mb-2">
            {state.guild.glimmers_sanctum_unlocked ? "✅ Unlocked" : "🔒 Locked"}
          </div>
          {!state.guild.glimmers_sanctum_unlocked && (
            <>
              <div className="text-[11px] text-neutral-400 mb-2">
                Requires <span className="text-white font-bold">Hall Lv {state.unlock_hall_level_required}</span> and{" "}
                <span className="text-amber-300 font-bold">{fmtNum(state.unlock_cost)} Guild Wealth</span>.
              </div>

              <div className="mb-3">
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-neutral-500">Wealth Progress</span>
                  <span className="text-amber-300 font-mono">
                    {fmtNum(state.guild.guild_wealth_display)} / {fmtNum(state.unlock_cost)}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-neutral-800 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500 to-purple-500 transition-all"
                    style={{ width: `${wealthPct}%` }}
                    data-testid="guild-progression-wealth-bar"
                  />
                </div>
                <div className="text-right text-[10px] text-purple-300 mt-0.5 font-bold">{wealthPct}%</div>
              </div>

              <button
                onClick={doUnlockSanctum}
                disabled={
                  busyUnlock ||
                  state.guild.hall_level < state.unlock_hall_level_required ||
                  state.guild.guild_wealth_display < state.unlock_cost
                }
                data-testid="guild-progression-sanctum-unlock"
                className={`w-full py-2.5 rounded-lg font-bold uppercase tracking-widest text-xs transition ${
                  busyUnlock ||
                  state.guild.hall_level < state.unlock_hall_level_required ||
                  state.guild.guild_wealth_display < state.unlock_cost
                    ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white hover:brightness-125"
                }`}
              >
                {busyUnlock ? "Unlocking..." : "🔓 Unlock Sanctum"}
              </button>
            </>
          )}
          {state.guild.glimmers_sanctum_unlocked && (
            <div className="text-[11px] text-emerald-300 mt-2 flex items-center gap-1.5">
              <ShieldCheck size={12} /> Sanctum unlocked · Glimmers below are available.
            </div>
          )}
        </div>
      </div>

      {/* Standard Shop */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={14} className="text-emerald-300" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-300">
            Standard Shop · Contribution Points
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="guild-standard-shop">
          {state.standard_shop.map((it) => (
            <ShopItemCard key={it.id} item={it} cp={state.cp} onBuy={buyShop} />
          ))}
        </div>
      </div>

      {/* Glimmers */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={14} className="text-purple-300" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-purple-300">
            Glimmers Sanctum · Active Combat Skills · CP or 🔥 Phoenix Blessings
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="guild-glimmers-grid">
          {state.glimmers.map((g) => (
            <GlimmerCard
              key={g.id}
              glimmer={g}
              cp={state.cp}
              pb={state.wallets.phoenix_blessings || 0}
              sanctumUnlocked={state.guild.glimmers_sanctum_unlocked}
              onBuy={buyGlimmer}
            />
          ))}
        </div>
      </div>

      {/* Info footer */}
      <div className="rounded-xl border border-neutral-800 bg-black/30 p-4 mt-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2 flex items-center gap-2">
          <Info size={12} /> Progression Notes
        </div>
        <ul className="list-disc list-inside space-y-1 text-[11px] text-neutral-400 leading-relaxed">
          <li>Chits and OD contributions are converted separately. Bound + Unbound OD wallets cannot be mixed on a single package.</li>
          <li>Sub-multiple contributions still credit the Guild Wealth pool but earn 0 personal CP.</li>
          <li>Every purchased item is <span className="text-amber-300 font-bold">Soulbound</span> — non-transferable, non-refundable.</li>
          <li>Phoenix Blessings are earned from the <span className="text-red-300">Chronophage Attack</span> event (300 PB on win, 150 PB on loss). Feed the Advanced Shop (Lv 6+) and offer an alternate path to buy Glimmers.</li>
          <li><span className="text-yellow-300">Savage Might</span> is auto-granted FREE at Pet Lv 10 — no purchase needed.</li>
        </ul>
      </div>
    </div>
  );
}
